/**
 * Mensagem do pagamento: o botão das listas de pagamentos abertos e o modal que ele abre (AF-4184).
 *
 * Fica ao lado do histórico de sinalizações no passo 1 da Identificação, no modal de identificar
 * pagamento e no de Pagamentos Não Identificados da tela de Títulos. ACRESCENTA ao que já foi escrito
 * e vale em qualquer estado do pagamento, inclusive o pendente com parte já efetivada, que a edição
 * do pagamento recusa.
 *
 * O modal vai para o `body` por portal, como o do histórico: as listas moram dentro de outros modais
 * e de linhas de tabela, e a camada (`zIndexClass`) é a de quem abriu.
 */
import { useState } from 'react';
import { createPortal } from 'react-dom';
import { FaCommentDots, FaTimes } from 'react-icons/fa';
import { toast } from 'react-toastify';

import { addPaymentNote } from '../../pages/billing/services/billingApi';

interface Props {
    paymentId: string;
    /** recarrega a lista, para a mensagem nova aparecer na coluna */
    onAdded: () => void;
    /** acima do modal de onde o botão foi aberto */
    zIndexClass?: string;
}

const AddPaymentNoteButton: React.FC<Props> = ({ paymentId, onAdded, zIndexClass = 'z-50' }) => {
    const [open, setOpen] = useState(false);
    const [text, setText] = useState('');
    const [saving, setSaving] = useState(false);
    const fieldId = `payment-note-${paymentId}`;

    const close = () => {
        setOpen(false);
        setText('');
    };

    const submit = async () => {
        setSaving(true);
        try {
            await addPaymentNote(paymentId, text.trim());
            toast.success('Mensagem adicionada.');
            close();
            onAdded();
        } catch (e: any) {
            toast.error(e?.message || 'Não foi possível adicionar a mensagem');
        } finally {
            setSaving(false);
        }
    };

    return (
        <>
            <button type="button" onClick={() => setOpen(true)}
                title="Adicionar mensagem ao pagamento" aria-label="Adicionar mensagem ao pagamento"
                className="px-2 py-1 align-middle text-gray-500 hover:text-blue-600">
                <FaCommentDots />
            </button>
            {open && createPortal(
                <div className={`fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center ${zIndexClass} p-4`}>
                    <div className="bg-white dark:bg-slate-800 rounded-lg shadow-xl p-6 max-w-md w-full text-left">
                        <div className="flex justify-between items-center mb-3">
                            <h2 className="text-lg font-semibold flex items-center gap-2">
                                <FaCommentDots className="text-blue-500" /> Adicionar mensagem
                            </h2>
                            <button onClick={close} aria-label="Fechar"
                                className="text-gray-400 hover:text-gray-600"><FaTimes size={20} /></button>
                        </div>

                        <p className="text-xs text-gray-500 mb-3">
                            Pagamento #{paymentId.slice(0, 8)} · o texto é ACRESCENTADO às mensagens existentes.
                        </p>

                        <label className="block text-sm font-medium mb-1" htmlFor={fieldId}>Mensagem</label>
                        <textarea id={fieldId} rows={3} value={text} onChange={e => setText(e.target.value)}
                            placeholder="O que se sabe sobre este dinheiro"
                            className="w-full text-sm border rounded p-2 mb-3 dark:bg-slate-700 dark:border-slate-600 dark:text-white" />

                        <div className="flex gap-2 justify-end">
                            <button onClick={close} disabled={saving}
                                className="px-4 py-2 text-sm rounded bg-gray-200 dark:bg-slate-600 hover:bg-gray-300">
                                Voltar
                            </button>
                            <button onClick={submit} disabled={saving || !text.trim()}
                                className="px-4 py-2 text-sm rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60">
                                {saving ? 'Salvando...' : 'Adicionar'}
                            </button>
                        </div>
                    </div>
                </div>,
                document.body,
            )}
        </>
    );
};

export default AddPaymentNoteButton;
